import React, { useState, useEffect } from 'react';

interface StorybookProps {
  onNavigateChapterBook?: () => void | Promise<void>;
  onNavigateGraphicNovel?: () => void | Promise<void>;
  onNavigatePictureBook?: () => void | Promise<void>;
  onNavigateStoryPlanner?: () => void | Promise<void>;
  onNavigateStoryStarter?: () => void | Promise<void>;
  onNavigateChapterMaster?: () => void | Promise<void>;
  onNavigateDailyWriter?: () => void | Promise<void>;
  onNavigateStoryArcBuilder?: () => void | Promise<void>;
}

const Storybook: React.FC<StorybookProps> = ({
  onNavigateChapterBook = () => {},
  onNavigateGraphicNovel = () => {},
  onNavigatePictureBook = () => {},
  onNavigateStoryPlanner = () => {},
  onNavigateStoryStarter = () => {},
  onNavigateChapterMaster = () => {},
  onNavigateDailyWriter = () => {},
  onNavigateStoryArcBuilder = () => {}
}) => {
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [writingStreak] = useState(5);
  const [totalWords] = useState(847);
  const [currentLevel] = useState(3);
  const [xpToNextLevel] = useState(250);
  const [currentXP] = useState(180);

  const theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  };

  const writingPrompts = [
    "A magical paintbrush brings your drawings to life!",
    "You discover a door in your closet that leads to different worlds...",
    "Your stuffed animal starts giving you advice!",
    "Every time you sneeze, something funny happens...",
    "You find a treasure map in your lunchbox!",
    "Your pet becomes a superhero for one day...",
    "The characters from your favorite book visit your school...",
    "You can talk to any animal for 24 hours..."
  ];

  useEffect(() => {
    const promptInterval = setInterval(() => {
      setCurrentPrompt((prev) => (prev + 1) % writingPrompts.length);
    }, 4000);
    return () => clearInterval(promptInterval);
  }, []);

  const IconComponent = ({ type, size = 24 }: { type: string; size?: number }) => {
    const icons: { [key: string]: React.ReactElement } = {
      book: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M4 2h16a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1zm1 2v16h14V4H5zm2 2h10v2H7V6zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/></svg>,
      star: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
      crown: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 4l3.5 2L12 2l5.5 4L21 4l-2 12H5z"/></svg>,
      flame: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/></svg>,
      gift: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-2 .89-2 2v4c0 1.11.89 2 2 2h1v6c0 1.11.89 2 2 2h10c1.11 0 2-.89 2-2v-6h1c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z"/></svg>,
      palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10c1.38 0 2.5-1.12 2.5-2.5 0-.61-.23-1.21-.64-1.67-.08-.09-.13-.21-.13-.33 0-.28.22-.5.5-.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9z"/></svg>,
      image: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>,
      target: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/><path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/><circle cx="12" cy="12" r="2"/></svg>,
      wand: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M7.5 5.6L10 7L8.6 4.5L10 2L7.5 3.4L5 2L6.4 4.5L5 7L7.5 5.6zM19.5 15.4L22 14L20.6 16.5L22 19L19.5 17.6L17 19L18.4 16.5L17 14L19.5 15.4zM22 2L20.6 4.5L22 7L19.5 5.6L17 7L18.4 4.5L17 2L19.5 3.4L22 2zM13.34 12.78L15.78 10.34L13.66 8.22L11.22 10.66L13.34 12.78zM14.37 7.29L16.71 9.63C17.1 10.02 17.1 10.65 16.71 11.04L5.04 22.71C4.65 23.1 4.02 23.1 3.63 22.71L1.29 20.37C0.9 19.98 0.9 19.35 1.29 18.96L12.96 7.29C13.35 6.9 13.98 6.9 14.37 7.29z"/></svg>,
      calendar: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>,
      mountain: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22l-9-12z"/></svg>,
      plan: <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/></svg>
    };
    return icons[type] || icons.book;
  };

  const getLevelProgress = () => ((currentXP / xpToNextLevel) * 100);

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Elements */}
      <div style={{ position: 'absolute', top: '10%', left: '5%', width: '60px', height: '60px', background: theme.pink, borderRadius: '50%', opacity: 0.1 }} />
      <div style={{ position: 'absolute', top: '20%', right: '10%', width: '40px', height: '40px', background: theme.success, borderRadius: '50%', opacity: 0.1 }} />
      <div style={{ position: 'absolute', bottom: '15%', left: '15%', width: '80px', height: '80px', background: theme.blue, borderRadius: '50%', opacity: 0.1 }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{
          background: 'white', borderRadius: '24px', padding: '32px', marginBottom: '32px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)', border: `4px solid ${theme.accent}`,
          position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: `linear-gradient(45deg, ${theme.pink}, ${theme.primary})`, borderRadius: '50%', opacity: 0.1 }} />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '20px' }}>
            <div>
              <h1 style={{
                fontSize: '3rem', fontWeight: '800',
                background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                margin: '0 0 12px 0', letterSpacing: '-0.02em'
              }}>
                Story Creator Studio
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{
                  background: `linear-gradient(45deg, ${theme.accent}, ${theme.primary})`,
                  color: 'white', padding: '8px 16px', borderRadius: '25px', fontSize: '1rem',
                  fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}>
                  <IconComponent type="crown" size={20} />
                  Level {currentLevel} Writer
                </div>
                <div style={{ fontSize: '1rem', color: '#555', fontWeight: '600' }}>
                  {currentXP}/{xpToNextLevel} XP to Level {currentLevel + 1}
                </div>
              </div>
            </div>
            
            <button style={{
              background: `linear-gradient(45deg, ${theme.pink}, ${theme.secondary})`,
              color: 'white', border: 'none', padding: '16px 28px', borderRadius: '20px',
              fontSize: '1.1rem', fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)', transition: 'all 0.3s ease',
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              <IconComponent type="gift" size={24} />
              Writer Rewards
            </button>
          </div>

          {/* XP Progress Bar */}
          <div style={{
            background: '#E5E5E5', borderRadius: '15px', height: '16px', overflow: 'hidden',
            marginBottom: '20px', border: '2px solid #DDD'
          }}>
            <div style={{
              background: `linear-gradient(90deg, ${theme.success}, ${theme.accent}, ${theme.primary})`,
              height: '100%', width: `${getLevelProgress()}%`, borderRadius: '15px',
              transition: 'width 0.8s ease', boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.3)'
            }} />
          </div>

          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
            <div style={{
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
              color: 'white', padding: '20px', borderRadius: '16px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '6px' }}>{totalWords}</div>
              <div style={{ fontSize: '1rem', opacity: 0.9, fontWeight: '600' }}>Words Written</div>
            </div>
            
            <div style={{
              background: `linear-gradient(135deg, ${theme.success}, ${theme.blue})`,
              color: 'white', padding: '20px', borderRadius: '16px', textAlign: 'center',
              position: 'relative', boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              {writingStreak >= 5 && (
                <div style={{
                  position: 'absolute', top: '-8px', right: '-8px', background: theme.accent,
                  color: '#333', borderRadius: '50%', width: '32px', height: '32px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', fontWeight: 'bold'
                }}>
                  <IconComponent type="flame" size={16} />
                </div>
              )}
              <div style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '6px' }}>{writingStreak}</div>
              <div style={{ fontSize: '1rem', opacity: 0.9, fontWeight: '600' }}>Day Streak</div>
            </div>
            
            <div style={{
              background: `linear-gradient(135deg, ${theme.pink}, ${theme.secondary})`,
              color: 'white', padding: '20px', borderRadius: '16px', textAlign: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '6px' }}>
                {currentXP} XP
              </div>
              <div style={{ fontSize: '1rem', opacity: 0.9, fontWeight: '600' }}>Experience Points</div>
            </div>
          </div>
        </div>

        {/* Daily Challenge */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.secondary}, ${theme.pink})`,
          borderRadius: '24px', padding: '32px', marginBottom: '40px', color: 'white',
          boxShadow: '0 15px 50px rgba(0,0,0,0.2)', position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-30px', left: '-30px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: '-20px', right: '-20px', width: '80px', height: '80px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          
          <div style={{
            position: 'absolute', top: '20px', right: '20px', background: 'rgba(255,255,255,0.25)',
            borderRadius: '25px', padding: '8px 16px', fontSize: '1rem', fontWeight: '700',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <IconComponent type="star" size={18} />
            +50 XP
          </div>
          <div style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '16px', opacity: 0.95 }}>
            Today's Creative Challenge
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: '700', lineHeight: '1.4' }}>
            {writingPrompts[currentPrompt]}
          </div>
        </div>

        {/* Story Planner Card - NEW */}
        <div style={{
          background: 'white', borderRadius: '24px', padding: '32px', marginBottom: '40px',
          boxShadow: '0 15px 50px rgba(0,0,0,0.15)', border: `4px solid ${theme.blue}`,
          position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: '-60px', left: '-60px', width: '200px', height: '200px', background: `linear-gradient(45deg, ${theme.blue}, ${theme.secondary})`, borderRadius: '50%', opacity: 0.05 }} />
          
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '24px', flexWrap: 'wrap', gap: '16px'
          }}>
            <div>
              <h2 style={{
                fontSize: '2rem', fontWeight: '800',
                background: `linear-gradient(45deg, ${theme.blue}, ${theme.secondary})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                margin: '0 0 8px 0'
              }}>
                Story Planner
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#666', margin: 0, fontWeight: '500' }}>
                Plan your perfect story before you start writing! Create characters, plot, and more.
              </p>
            </div>
            
            <div style={{
              background: `linear-gradient(45deg, ${theme.blue}, ${theme.secondary})`,
              color: 'white', padding: '12px 20px', borderRadius: '20px', fontSize: '1rem',
              fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
            }}>
              <IconComponent type="star" size={20} />
              +100 XP
            </div>
          </div>

          <button
            onClick={onNavigateStoryPlanner}
            style={{
              background: `linear-gradient(45deg, ${theme.blue}, ${theme.secondary})`,
              color: 'white', border: 'none', padding: '16px 32px', borderRadius: '20px',
              fontSize: '1.1rem', fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)', transition: 'all 0.3s ease',
              display: 'flex', alignItems: 'center', gap: '10px'
            }}
          >
            <IconComponent type="plan" size={20} />
            Start Planning Your Story
          </button>
        </div>

        {/* Story Creation Cards */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: '32px', marginBottom: '40px'
        }}>
          {[
            { 
              type: 'book', 
              title: 'Chapter Books', 
              desc: 'Create epic multi-chapter adventures with heroes, villains, and amazing plot twists!', 
              bg: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`, 
              xp: '+15 XP per chapter', 
              stories: '3 stories in progress', 
              onClick: onNavigateChapterBook,
              color: theme.primary
            },
            { 
              type: 'palette', 
              title: 'Graphic Novels', 
              desc: 'Tell visual stories with comics, speech bubbles, and awesome illustrations!', 
              bg: `linear-gradient(135deg, ${theme.blue}, ${theme.secondary})`, 
              xp: '+20 XP per page', 
              stories: 'Ready to create', 
              onClick: onNavigateGraphicNovel,
              color: theme.blue
            },
            { 
              type: 'image', 
              title: 'Picture Books', 
              desc: 'Perfect for younger readers with beautiful pictures and simple, fun stories!', 
              bg: `linear-gradient(135deg, ${theme.success}, ${theme.pink})`, 
              xp: '+25 XP complete', 
              stories: '1 story complete', 
              onClick: onNavigatePictureBook,
              color: theme.success
            }
          ].map((item, index) => (
            <div key={index} onClick={item.onClick} style={{
              background: 'white', borderRadius: '24px', padding: '40px', textAlign: 'center',
              cursor: 'pointer', border: `4px solid ${item.color}`,
              boxShadow: '0 15px 50px rgba(0,0,0,0.15)', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative', overflow: 'hidden'
            }}>
              <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '120px', height: '120px', background: item.bg, borderRadius: '50%', opacity: 0.1 }} />
              
              <div style={{
                position: 'absolute', top: '16px', right: '16px', background: item.bg,
                color: 'white', borderRadius: '16px', padding: '8px 12px', fontSize: '0.9rem',
                fontWeight: '700', boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
              }}>
                {item.xp}
              </div>
              
              <div style={{
                width: '80px', height: '80px', background: item.bg, borderRadius: '20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 24px', color: 'white', boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
              }}>
                <IconComponent type={item.type} size={40} />
              </div>
              
              <h3 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#333', margin: '0 0 16px 0' }}>
                {item.title}
              </h3>
              
              <p style={{ color: '#666', fontSize: '1.1rem', lineHeight: '1.6', margin: '0 0 24px 0' }}>
                {item.desc}
              </p>
              
              <div style={{
                background: `${item.color}15`, borderRadius: '12px', padding: '12px 16px',
                fontSize: '1rem', fontWeight: '600', color: item.color, display: 'inline-block',
                border: `2px solid ${item.color}30`
              }}>
                {item.stories}
              </div>
            </div>
          ))}
        </div>

        {/* Writing Helpers */}
        <div style={{
          background: 'rgba(255,255,255,0.95)', borderRadius: '20px', padding: '24px', marginBottom: '32px',
          border: `2px solid ${theme.accent}30`, boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '1.4rem', fontWeight: '700', color: '#555', margin: 0,
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              <IconComponent type="wand" size={24} />
              Writing Helper Tools
            </h3>
            <div style={{ fontSize: '0.95rem', color: '#999', fontStyle: 'italic' }}>
              Quick tools to boost your creativity
            </div>
          </div>
          
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px'
          }}>
            {[
              { id: 'story-starter', title: 'Story Starter', icon: 'wand', color: theme.primary, onClick: onNavigateStoryStarter },
              { id: 'chapter-master', title: 'Chapter Master', icon: 'book', color: theme.blue, onClick: onNavigateChapterMaster },
              { id: 'daily-writer', title: 'Daily Writer', icon: 'calendar', color: theme.pink, onClick: onNavigateDailyWriter }
            ].map((tool) => (
              <button key={tool.id} onClick={tool.onClick} style={{
                background: 'white', border: `2px solid ${tool.color}50`, borderRadius: '12px',
                padding: '16px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
              }}>
                <div style={{
                  width: '40px', height: '40px', background: `${tool.color}20`, borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px', color: tool.color
                }}>
                  <IconComponent type={tool.icon} size={22} />
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#333' }}>
                  {tool.title}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Storybook;